import React, { useCallback, useMemo, useState } from 'react'
import { Col, List, Row, Tooltip, Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  Agency,
  AirlinesCodes,
  Id,
  PhoneContact,
  Request,
  RequestQuote,
  RequestQuoteAir,
  RequestQuoteAirFare,
  RequestQuoteAirFlight,
  RequestTicketSelectedSituation,
  RequestTicketStatus,
  RequestTicketUserData,
  Timestamp
} from 'src/types'
import {
  AirBadgeBaggage,
  Empty,
  Spacer,
  T,
  Text,
  TextTValue,
  Tstring
} from 'src/components/atoms'
import { ApplicationState } from 'src/store'
import {
  AvatarListHorizontal,
  TagCurrency,
  TagTicketSituation
} from 'src/components/molecules'
import { ButtonTitled } from 'src/components/Ant'
import { getAirHistory } from 'src/core/store/ducks/air/actions'
import { OriginRequest } from 'src/core/store/ducks/requests/types'
import { PhoneMask } from 'src/core/utils/phoneMask'
import {
  saveFullRequest,
  saveRequestTickets
} from 'src/core/store/ducks/requests/actions'
import { stringify } from 'src/core/hooks/use-translation'
import getNameOfTheAirline from 'src/components/atoms/get-name-of-the-airline'
import Images from 'src/config/images'
import ModalFormEdit from 'src/views/Requests/views/Form/components/Steps/Services/components/Air/components/modal-form/index'
import ModalHistory from 'src/views/Requests/views/Form/components/Steps/Services/components/Air/components/modal-history'
import useSelectedAirs from 'src/components/atoms/use-get-selected-airs'
import * as CSS from './styles'

interface Props {
  approvals?: boolean
  originRequest?: OriginRequest
  request: Request
}

type AirWay = Pick<
  RequestQuoteAir,
  | 'air_destiny'
  | 'air_origin'
  | 'company'
  | 'request_quote_air_flights'
  | 'stops'
> &
  Pick<
    RequestQuoteAirFare,
    | 'baggage_amount'
    | 'baggage_weight'
    | 'baggage'
    | 'family'
    | 'request_quote_fare_hash'
    | 'total_value'
  > &
  Pick<RequestQuote, 'expire_date'> & {
    arrival_date_number: number // to sort
    arrival_date: string
    arrival_time: string
    departure_date: string
    departure_time: string
  }

type SelectedAir = {
  // informações do ticket
  agency_data: Agency
  booking_code: string
  eticket_code: string
  request_ticket_id?: Id
  situation: RequestTicketSelectedSituation
  status: RequestTicketStatus
  total_value?: number
  user_data: RequestTicketUserData[]

  // informações do trecho
  request_quote_air_one_way: AirWay
  request_quote_air_round_trip: AirWay
}

function ExpirationIcon({ expire_date }: { expire_date: Timestamp }) {
  const { t } = useTranslation()
  if (!expire_date) {
    return <></>
  }

  let src = Images.icons.planeRed

  const expireDate = moment.utc(expire_date * 1000)

  if (moment().utc().isBefore(expireDate)) {
    const diffInHours = moment().diff(moment.utc(expire_date * 1000), 'hours')

    if (diffInHours >= -6) {
      src = Images.icons.planeYellow
    } else {
      src = Images.icons.planeGreen
    }
  }

  const title = t('web.services.airs.expiration', {
    value: moment.unix(expire_date).format('DD/MM/YYYY HH:mm:ss')
  })

  return <CSS.Plane src={src} title={title} />
}

function AirCompanyImage({ item }: { item: AirlinesCodes }) {
  return (
    <img
      alt={Tstring('web.services.airs.img-alt')}
      src={Images.airlines[item] || Images.noImage}
      style={{
        maxWidth: 50,
        minWidth: 20
      }}
    />
  )
}

function RenderStops({ stops }: { stops: number }): JSX.Element {
  if (stops <= 0) {
    return <CSS.Stops stops={0} title="web.services.airs.form.stop_no" />
  }

  return (
    <CSS.Stops
      stops={stops}
      title={stringify('web.services.airs.form.stop', { count: stops })}
    />
  )
}

function RenderSummaryAirWayStep({
  item
}: {
  item: RequestQuoteAirFlight
}): JSX.Element {
  function momentFormat(number: number, template: string) {
    return moment.unix(number).utc().format(template)
  }

  const company = getNameOfTheAirline(item.company)

  return (
    <CSS.AirWayStep>
      <CSS.AirWayStepItem>
        <Text
          suffix=" "
          text="web.services.airs.operated-by"
          type="secondary"
        />
        <Text strong>{company}</Text>
      </CSS.AirWayStepItem>
      <CSS.AirWayStepItem>
        <Text type="secondary">
          {momentFormat(item.departure_date, 'DD/MM/YYYY')}
        </Text>
        <Text strong>{momentFormat(item.departure_date, 'HH:mm')}</Text>
        <Text type="secondary">{item.flight_origin}</Text>
      </CSS.AirWayStepItem>
      <CSS.AirWayStepItem>
        <span>{`Voo ${item.flight_code}`}</span>
        <Text
          strong
          text={{
            key: 'web.services.airs.form.minutes',
            options: { count: item.duration }
          }}
        />
      </CSS.AirWayStepItem>
      <CSS.AirWayStepItem>
        <Text type="secondary">
          {momentFormat(item.arrival_date, 'DD/MM/YYYY')}
        </Text>
        <Text strong>{momentFormat(item.arrival_date, 'HH:mm')}</Text>
        <Text type="secondary">{item.flight_destiny}</Text>
      </CSS.AirWayStepItem>
    </CSS.AirWayStep>
  )
}

function RenderSummaryAirWay({
  item
}: {
  item: AirWay
  isApproval?: boolean
}): JSX.Element {
  return (
    <>
      <CSS.AirWay>
        <AirCompanyImage item={item.company} />
        <CSS.DivWay>
          <CSS.ToCenter>
            <Text type="secondary">{item.departure_date}</Text>
            <br />
            <CSS.Time>{item.departure_time}</CSS.Time>
            <br />
            <Text type="secondary">{item.air_origin}</Text>
          </CSS.ToCenter>
          <CSS.DivStop>
            <RenderStops stops={item.stops} />
          </CSS.DivStop>
          <CSS.ToCenter>
            <Text type="secondary">{item.arrival_date}</Text>
            <br />
            <CSS.Time>{item.arrival_time}</CSS.Time>
            <br />
            <Text type="secondary">{item.air_destiny}</Text>
          </CSS.ToCenter>
        </CSS.DivWay>
        <div>
          <Tooltip title={<T text="web.services.airs.form.fare.family" />}>
            {item.family}
          </Tooltip>
          <br />
          <AirBadgeBaggage
            baggage={item.baggage}
            baggage_amount={item.baggage_amount}
            baggage_weight={item.baggage_weight}
          />
        </div>
      </CSS.AirWay>
      {item.request_quote_air_flights.map(request_quote_air_flight => (
        <RenderSummaryAirWayStep
          item={request_quote_air_flight}
          key={request_quote_air_flight.flight_code}
        />
      ))}
    </>
  )
}

function RenderItemListAirs({
  approvals,
  item,
  removeTicket
}: {
  approvals: boolean
  item: SelectedAir
  removeTicket: (id: Id) => void
}) {
  const {
    me: {
      user: { tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const emissionContacts: PhoneContact[] =
    item.agency_data?.agency_organization.contacts?.reduce<PhoneContact[]>(
      (acc, cur) => {
        if (cur.hasOwnProperty('phone_contacts')) {
          acc.push(
            ...{ phone_contacts: null, email_contacts: null, ...cur }
              .phone_contacts
          )
        }
        return acc
      },
      []
    ) || []

  const canRemoveTicket =
    +tenant_id === 559042084949 && typeof item.request_ticket_id === 'number'

  return (
    <CSS.AirItem>
      <List.Item.Meta
        description={
          <>
            {/* Top */}
            <CSS.DivBordered>
              <Row>
                <Col span={24}>
                  <CSS.CardTopDiv>
                    <div>
                      <AvatarListHorizontal users={item.user_data} />
                    </div>
                    {!!item.status && (
                      <div>
                        {approvals && (
                          <ExpirationIcon
                            expire_date={
                              item.request_quote_air_one_way.expire_date
                            }
                          />
                        )}
                        <TagTicketSituation status={item.situation} />
                        {canRemoveTicket && (
                          <ButtonTitled
                            icon="delete"
                            key="hotel-delete"
                            onClick={() => removeTicket(item.request_ticket_id)}
                            size="default"
                            style={{ marginLeft: 8 }}
                            tooltip="common.actions.remove"
                          />
                        )}
                      </div>
                    )}
                    {!!item.eticket_code && (
                      <div>
                        <Spacer horizontal="4px" />
                        <Text
                          strong
                          suffix=":"
                          text="web.services.airs.booking-code"
                        />
                        <CSS.Code>
                          <span>
                            <Text code copyable={{ text: item.eticket_code }}>
                              {item.eticket_code}
                            </Text>
                          </span>
                        </CSS.Code>
                      </div>
                    )}
                  </CSS.CardTopDiv>
                </Col>
              </Row>
            </CSS.DivBordered>
            {/* Air Infos content */}
            <CSS.DivBordered>
              <Row>
                <Col span={24}>
                  <RenderSummaryAirWay
                    isApproval={approvals}
                    item={item.request_quote_air_one_way}
                  />
                  {!!item.request_quote_air_round_trip
                    ?.request_quote_fare_hash && (
                    <RenderSummaryAirWay
                      isApproval={approvals}
                      item={item.request_quote_air_round_trip}
                    />
                  )}
                </Col>
              </Row>
            </CSS.DivBordered>
            {/* Emission infos */}
            <CSS.DivBordered>
              <CSS.AgencyInfo>
                <div>
                  <TextTValue text="web.agencies.Issuer">
                    {item.agency_data?.agency_organization.trade_name}
                  </TextTValue>
                  {emissionContacts.map((phone_contacts, index) => (
                    <div
                      key={index}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Text
                        strong
                        suffix=": "
                        text="web.agencies.contacts.phone"
                      />
                      <Text
                        code
                        copyable={{
                          text: phone_contacts.phone_number
                        }}
                      >
                        {`${phone_contacts.calling_code} ${PhoneMask(
                          phone_contacts.phone_number
                        )}`}
                      </Text>
                      {phone_contacts.is_whatsapp && (
                        <CSS.IconWhatsApp
                          alt="WhatsApp"
                          src={Images.logoWhatsSmall}
                          onClick={() => {
                            window.open(
                              `https://wa.me/${phone_contacts.phone_number}`
                            )
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <TagCurrency value={item.total_value} />
                </div>
              </CSS.AgencyInfo>
            </CSS.DivBordered>
          </>
        }
      />
    </CSS.AirItem>
  )
}

function AirsSummary({ approvals, request, originRequest }: Props) {
  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const selectedAirs = useSelectedAirs(request)

  const [visibleModalEdit, setVisibleModalEdit] = useState<boolean>(false)

  function handleRemoveTicket(id: Id) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.approvals.modal-remove-ticket'),
      okText: t('common.actions.yes'),
      onCancel() {},
      onOk: () => {
        return new Promise(() => {
          const foundTicket = request.request_tickets.find(
            ticket => +ticket.request_ticket_id === +id
          )
          if (foundTicket) {
            dispatch(saveRequestTickets([{ ...foundTicket, active: false }]))
          }
        })
      },
      title: t('common.are-you-sure')
    })
  }

  const handleDeleteTicketsAir = useCallback(() => {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.approvals.modal-remove-air'),
      okText: t('common.actions.yes'),
      onCancel() {},
      onOk: async () => {
        return new Promise(() => {
          dispatch(
            saveFullRequest({
              ...request,
              request_tickets: request.request_tickets.map(ticket =>
                ticket.request_ticket_type !== 'AIR'
                  ? { ...ticket }
                  : {
                      ...ticket,
                      active: false
                    }
              )
            })
          )
        })
      },
      title: t('common.are-you-sure')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request])

  const extraButtons = useMemo(
    () => {
      if (selectedAirs.length <= 0) {
        return [
          approvals && (
            <ButtonTitled
              icon="plus"
              key="air-new"
              onClick={() => setVisibleModalEdit(true)}
              size="default"
              style={{ marginLeft: 8 }}
              tooltip="common.actions.add"
            />
          )
        ]
      }

      return [
        approvals && (
          <ButtonTitled
            icon="edit"
            key="air-edit"
            onClick={() => setVisibleModalEdit(true)}
            size="default"
            style={{ marginLeft: 8 }}
            tooltip="common.actions.edit"
          />
        ),
        approvals && (
          <ButtonTitled
            icon="delete"
            key="air-delete"
            onClick={handleDeleteTicketsAir}
            size="default"
            style={{ marginLeft: 8 }}
            tooltip="common.actions.remove-all"
          />
        ),
        typeof request.request_id === 'number' && (
          <ButtonTitled
            key="air-history"
            icon="history"
            onClick={() => {
              if (request?.request_id) {
                dispatch(getAirHistory({ request_id: request.request_id }))
              }
              setVisibleModalHistory(true)
            }}
            size="default"
            tooltip="common.actions.historical"
            style={{ marginLeft: 8 }}
          />
        )
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approvals, selectedAirs]
  )

  return (
    <>
      <CSS.Card
        data-test-id="summary-airs"
        bodyStyle={{ paddingTop: 0, minHeight: 220 }}
        extra={extraButtons}
        title={<T text="web.services.airs.title" />}
      >
        <List
          dataSource={selectedAirs}
          itemLayout="horizontal"
          locale={{ emptyText: <Empty /> }}
          renderItem={(item, index) => (
            <RenderItemListAirs
              approvals={approvals}
              item={item}
              key={index}
              removeTicket={handleRemoveTicket}
            />
          )}
        />
      </CSS.Card>
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
      <ModalFormEdit
        approvals={approvals}
        request={request}
        toggle={() => {
          setVisibleModalEdit(false)
        }}
        visible={visibleModalEdit}
      />
    </>
  )
}

export default AirsSummary
